import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { DailyLabellerStatsEntry } from "src/store/api/generatedApi";
import { CsvColumnDef } from "src/utils/csv";
import { SessionWithTenant } from "./dailySessionPanel";

type Translate = (key: string) => string;
type FormatNumber = (value: number) => string;

export type DailyActivityRow = Omit<DailyLabellerStatsEntry, "sessions"> & {
  id: string;
  sessions: SessionWithTenant[];
};

export type SessionCsvRow = SessionWithTenant & {
  date_labelled: string;
  labeller_name: string;
  url: string;
};

const SESSION_TIMESTAMP_FORMAT = "YYYY-MM-DD HH:mm";

const formatHoursMinutes = (startedAt: string, endedAt: string): string => {
  const seconds = Math.max(0, dayjs(endedAt).diff(dayjs(startedAt), "second"));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const buildDailyActivityColumns = (
  t: Translate,
  formatNumber1dp: FormatNumber
): GridColDef<DailyActivityRow>[] => [
  {
    field: "date",
    headerName: t("date"),
    width: 130,
  },
  {
    field: "time_labelled_seconds",
    headerName: t("totalTime"),
    type: "number",
    width: 140,
    valueGetter: (_value, row) => row.time_labelled_seconds / 3600,
    valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
  },
  {
    field: "time_spent_labelling_seconds",
    headerName: t("totalTimeSpent"),
    type: "number",
    width: 170,
    valueGetter: (_value, row) => row.time_spent_labelling_seconds / 3600,
    valueFormatter: (value: number) => `${formatNumber1dp(value)}h`,
  },
  {
    field: "submissions",
    headerName: t("totalSubmissions"),
    type: "number",
    width: 120,
  },
  {
    field: "label_hours_per_hour",
    headerName: t("hoursPerHour"),
    type: "number",
    width: 120,
    valueGetter: (_value, row) =>
      row.time_spent_labelling_seconds > 0
        ? row.time_labelled_seconds / row.time_spent_labelling_seconds
        : undefined,
    valueFormatter: (value?: number) =>
      value != null ? formatNumber1dp(value) : "—",
  },
];

export const buildDailyActivitySessionCsvColumns = (
  t: Translate
): CsvColumnDef<SessionCsvRow>[] => [
  { header: t("labeller"), value: (row) => row.labeller_name },
  { header: t("dateLabelled"), value: (row) => row.date_labelled },
  { header: t("type"), value: (row) => t(row.session_type) },
  { header: t("location"), value: (row) => row.location_name },
  {
    header: t("footageStart"),
    value: (row) =>
      dayjs.utc(row.footage_start_at).format(SESSION_TIMESTAMP_FORMAT),
  },
  {
    header: t("footageEnd"),
    value: (row) =>
      dayjs.utc(row.footage_end_at).format(SESSION_TIMESTAMP_FORMAT),
  },
  {
    header: t("duration"),
    value: (row) =>
      formatHoursMinutes(row.footage_start_at, row.footage_end_at),
  },
  { header: "URL", value: (row) => row.url },
];
